import { Modal, Form, Button, Typography, Checkbox, Space, Flex, Alert, Spin } from 'antd'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import React, { useEffect, useState } from 'react'

import { SchoolService } from '@services/SchoolService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { Subscription } from '../../../../types'

import { BillableActionsTable } from './BillableActionsTable'

dayjs.extend(weekday)
dayjs.extend(localeData)

interface EditSubscriptionModalProps {
    isOpen: boolean
    onClose: () => void
    subscription: Subscription
    schoolId: string
    seatCount: number
    refetch: () => Promise<void>
    onSuccess: () => void
}

export const CancelSubscriptionModal: React.FC<EditSubscriptionModalProps> = ({
    isOpen,
    onClose,
    schoolId,
    subscription,
    seatCount,
    onSuccess,
    refetch
}) => {
    const confirm = useConfirmation()
    const [form] = Form.useForm()
    const [isLoading, setIsLoading] = useState(false)
    const { showNotification } = useNotifications()
    const [showBillables, setShowBillables] = useState<boolean>(false)
    const issueRefund = Form.useWatch('issueRefund', form)

    useEffect(() => {
        form.setFieldsValue({
            issueRefund: false
        })
    }, [form])

    const handleSubmit = async (values: any) => {
        if (
            await confirm({
                title: 'Are you sure you want to cancel the subscription?',

                confirmType: 'danger'
            })
        ) {
            try {
                setIsLoading(true)
                await SchoolService.cancelSubscription(schoolId, subscription.id, values.issueRefund)
                onSuccess()
                onClose()
                await refetch()
            } catch (error) {
                showNotification({
                    title: 'Error cancelling subscription',
                    type: 'error'
                })
            } finally {
                setIsLoading(false)
            }
        }
    }

    if (!subscription) {
        return null
    }

    if (isLoading) {
        return <Spin />
    }

    const width = showBillables ? 700 : 400

    return (
        <Modal width={width} title="Cancel Subscription" open={isOpen} onCancel={onClose} footer={null}>
            {subscription.stripeId && <Typography.Text>Stripe Subscriptions must be edited in Stripe </Typography.Text>}

            {!subscription.stripeId && (
                <Form form={form} onFinish={handleSubmit}>
                    <Space vertical style={{ width: '100%' }} size="large">
                        {!subscription.isInternal && (
                            <>
                                <Space vertical>
                                    <Form.Item
                                        name="issueRefund"
                                        label="Issue Refund"
                                        valuePropName="checked"
                                        initialValue={false}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Checkbox />
                                    </Form.Item>
                                    <Typography.Text style={{ fontSize: '10px' }}>
                                        Refund Policy: Refunds are to the discretion of the manager in charge
                                    </Typography.Text>
                                </Space>

                                {issueRefund && (
                                    <Alert
                                        title={`This creator will be refunded ${Math.abs(
                                            seatCount
                                        )} seat(s) at the next billing cycle`}
                                        type="warning"
                                    />
                                )}
                            </>
                        )}

                        <Flex justify="space-between">
                            <Form.Item>
                                <Button danger type="primary" htmlType="submit">
                                    Proceed with Cancellation
                                </Button>
                            </Form.Item>
                            {!subscription.isInternal && (
                                <Button type="link" onClick={() => setShowBillables(!showBillables)}>
                                    {showBillables ? 'Hide Billables' : 'View billables'}
                                </Button>
                            )}
                        </Flex>
                    </Space>
                </Form>
            )}
            {showBillables && (
                <>
                    <Typography.Title level={3}>All billable actions on this subscription</Typography.Title>
                    <BillableActionsTable schoolId={schoolId} subscriptionId={subscription.id}></BillableActionsTable>
                </>
            )}
        </Modal>
    )
}
